import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../shared/api/api.js';
import { 
  Calendar, 
  User, 
  ArrowRight, 
  Search, 
  BookOpen, 
  Clock,
  Sparkles,
  ChevronRight,
  Bookmark,
  Award,
  Tag,
  Layers,
  RotateCcw,
  X,
  Filter,
} from 'lucide-react';
import { useBookmarks } from '../hooks/useBookmarks.js';
import { useNotificationStore } from '../../notifications/store/useNotificationStore.js';
import { getCategoryBadgeStyle, getCategoryDotStyle } from './CategoryManager.js';
import { type BlogCategory } from '../api/blogApi.js';
import { SeoHead, createCollectionPageSchema, BreadcrumbNav } from '../../../shared/components/index.js';

interface BlogListViewProps {
  onSelectPost?: (slug: string) => void;
}

export const BlogListView: React.FC<BlogListViewProps> = ({ onSelectPost }) => {
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'bookmarks'>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const { bookmarks, isBookmarked, toggleBookmark } = useBookmarks();
  
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmittingNewsletter, setIsSubmittingNewsletter] = useState(false);
  const showToast = useNotificationStore((state) => state.showToast);

  const handleSubscribeNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail) {
      showToast('Error', 'Alamat email wajib diisi', 'error');
      return;
    }
    setIsSubmittingNewsletter(true);
    try {
      const res = await api.subscribeNewsletter(newsletterEmail);
      if (res.success) {
        showToast('Berhasil', res.message || 'Berhasil berlangganan newsletter UIBN!', 'success');
        setNewsletterEmail('');
      } else {
        showToast('Info', res.message || 'Ada informasi langganan.', 'info');
      }
    } catch (err: any) {
      showToast('Gagal', err.message || 'Gagal berlangganan newsletter', 'error');
    } finally {
      setIsSubmittingNewsletter(false);
    }
  };

  const { data: posts, isLoading } = useQuery({
    queryKey: ['blog-posts', search],
    queryFn: () => api.getPublishedPosts(search),
  });

  const { data: officialCategories } = useQuery({
    queryKey: ['blog-categories'],
    queryFn: () => api.getBlogCategories(),
  });

  // Only use official categories registered in the database
  const availableCategories = useMemo(() => {
    if (!officialCategories || officialCategories.length === 0) return [];
    
    return officialCategories.map((cat) => {
      const count = (posts || []).filter((post) => {
        const isIdMatch = post.categoryId === cat.id;
        const isNameMatch = post.category && post.category.toLowerCase().trim() === cat.name.toLowerCase().trim();
        const isSlugMatch = post.category && post.category.toLowerCase().trim() === cat.slug.toLowerCase().trim();
        return isIdMatch || isNameMatch || isSlugMatch;
      }).length;

      return {
        ...cat,
        activeCount: count,
      };
    });
  }, [officialCategories, posts]);

  // Find active category object if selected
  const activeCategoryObj = useMemo(() => {
    if (selectedCategory === 'all') return null;
    return officialCategories?.find(
      (c) => c.id === selectedCategory || c.slug === selectedCategory || c.name.toLowerCase() === selectedCategory.toLowerCase()
    ) || null;
  }, [officialCategories, selectedCategory]);

  // Filter posts based on bookmark status and selected official category
  const filteredPosts = useMemo(() => {
    if (!posts) return [];
    return posts.filter((post) => {
      if (filterMode === 'bookmarks' && !isBookmarked(post.slug)) {
        return false;
      }
      if (selectedCategory !== 'all') {
        if (activeCategoryObj) {
          const isIdMatch = post.categoryId === activeCategoryObj.id;
          const isNameMatch = post.category && post.category.toLowerCase().trim() === activeCategoryObj.name.toLowerCase().trim();
          const isSlugMatch = post.category && post.category.toLowerCase().trim() === activeCategoryObj.slug.toLowerCase().trim();
          if (!isIdMatch && !isNameMatch && !isSlugMatch) {
            return false;
          }
        } else {
          return false;
        }
      }
      return true;
    });
  }, [posts, filterMode, selectedCategory, activeCategoryObj, isBookmarked]);

  const featuredPost = filteredPosts.find((post) => post.isFeatured === 1);
  const regularPosts = featuredPost && filterMode === 'all' && selectedCategory === 'all' && !search
    ? filteredPosts.filter((post) => post.id !== featuredPost.id)
    : filteredPosts;

  const getPostCategory = (post: { categoryId?: string | null; category?: string | null }) => {
    if (!officialCategories) return null;
    return officialCategories.find(
      (c) => c.id === post.categoryId || (post.category && c.name.toLowerCase().trim() === post.category.toLowerCase().trim())
    ) || null;
  };

  const blogStructuredData = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://uibn.id';
    const items = (posts || []).map((p) => ({
      name: p.title,
      url: `${origin}/blog/${p.slug}`,
      description: p.excerpt || p.title,
    }));
    return createCollectionPageSchema(
      'Blog & Wawasan Literasi | UIBN',
      'Temukan panduan penulisan, industri penerbitan, dan artikel edukasi perbukuan nasional di Blog UIBN.',
      `${origin}/blog`,
      items
    );
  }, [posts]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10">
      <SeoHead
        title="Blog UIBN – Wawasan Penulisan & Penerbitan Buku"
        description="Temukan panduan, tren industri penerbitan, dan tips praktis untuk penulis serta penerbit dalam merilis naskah berkualitas."
        keywords={['blog uibn', 'penerbitan buku', 'tips menulis', 'hak cipta', 'registrasi buku', 'literasi indonesia']}
        structuredData={blogStructuredData}
      />

      <div className="mb-6">
        <BreadcrumbNav
          items={[
            { name: 'Beranda', url: '/' },
            { name: 'Blog UIBN', url: '/blog' },
          ]}
        />
      </div>

      {/* Header */}
      <div className="text-center mb-8 sm:mb-12 space-y-2.5 sm:space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-[11px] sm:text-xs font-bold uppercase tracking-wider">
          <BookOpen className="w-3.5 h-3.5" />
          <span>Wawasan Dunia Literasi</span>
        </div>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
          Blog UIBN: Penulisan & Penerbitan
        </h1>
        <p className="text-sm sm:text-lg text-slate-600 max-w-2xl mx-auto">
          Temukan panduan, tren industri, dan tips praktis untuk penulis dan penerbit dalam mengembangkan karya berkualitas.
        </p>
      </div>

      {/* Tabs and Search Container */}
      <div className="max-w-xl mx-auto mb-6 sm:mb-8 space-y-3 sm:space-y-4">
        {/* Filter Tabs */}
        <div className="flex bg-slate-100 p-1 rounded-xl sm:rounded-2xl border border-slate-200">
          <button
            type="button"
            onClick={() => setFilterMode('all')}
            className={`flex-1 py-2 sm:py-3 min-h-[44px] text-xs sm:text-sm font-bold rounded-lg sm:rounded-xl transition-all cursor-pointer flex items-center justify-center ${
              filterMode === 'all'
                ? 'bg-white text-indigo-600 shadow-xs'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            Semua Artikel ({posts?.length || 0})
          </button>
          <button
            type="button"
            onClick={() => setFilterMode('bookmarks')}
            className={`flex-1 py-2 sm:py-3 min-h-[44px] text-xs sm:text-sm font-bold rounded-lg sm:rounded-xl transition-all flex items-center justify-center gap-1.5 sm:gap-2 cursor-pointer ${
              filterMode === 'bookmarks'
                ? 'bg-white text-amber-600 shadow-xs'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <Bookmark className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${filterMode === 'bookmarks' ? 'fill-current text-amber-500' : ''}`} />
            Disimpan ({bookmarks.length})
          </button>
        </div>

        {/* Search Input */}
        <div className="relative group">
          <Search className="absolute left-3.5 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
          <input
            type="text"
            placeholder={
              filterMode === 'bookmarks'
                ? 'Cari artikel tersimpan...'
                : 'Cari topik atau kata kunci artikel...'
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 sm:pl-12 pr-9 sm:pr-10 py-3 sm:py-3.5 bg-white border border-slate-200 rounded-xl sm:rounded-2xl shadow-xs focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-xs sm:text-sm"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition cursor-pointer"
              title="Hapus pencarian"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Category Filters Rail - Ultra Compact & Responsive */}
      {availableCategories.length > 0 && (
        <div className="max-w-5xl mx-auto mb-6 sm:mb-10">
          <div className="sm:bg-slate-50/80 sm:border sm:border-slate-200/80 sm:rounded-3xl sm:p-4 sm:shadow-2xs">
            {/* Desktop-only Header row */}
            <div className="hidden sm:flex items-center justify-between gap-3 mb-3 px-1">
              <div className="flex items-center gap-2 text-slate-800">
                <div className="w-5 h-5 rounded-md bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                  <Layers className="w-3 h-3" />
                </div>
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Kategori Artikel
                </h3>
                <span className="text-[11px] font-medium text-slate-400">
                  • {availableCategories.length} Rubrik
                </span>
              </div>

              {selectedCategory !== 'all' && (
                <button
                  type="button"
                  onClick={() => setSelectedCategory('all')}
                  className="text-xs font-bold text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1.5 cursor-pointer py-1 px-2 rounded-lg hover:bg-indigo-50/60 transition"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Semua Kategori</span>
                </button>
              )}
            </div>

            {/* Horizontal Scrollable Rail on Mobile, Clean Wrap on Desktop */}
            <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto sm:flex-wrap pb-2 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <button
                type="button"
                id="filter-category-all"
                onClick={() => setSelectedCategory('all')}
                className={`px-3.5 py-2 min-h-[44px] rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center justify-center gap-1.5 ${
                  selectedCategory === 'all'
                    ? 'bg-indigo-600 text-white shadow-xs ring-1 sm:ring-2 ring-indigo-600/20'
                    : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 shadow-2xs'
                }`}
              >
                <span>Semua</span>
                <span
                  className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono font-bold tabular-nums ${
                    selectedCategory === 'all' ? 'bg-indigo-700 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {posts?.length || 0}
                </span>
              </button>

              {availableCategories.map((cat) => {
                const isSelected =
                  selectedCategory === cat.id ||
                  selectedCategory === cat.slug ||
                  selectedCategory.toLowerCase() === cat.name.toLowerCase();

                return (
                  <button
                    key={`cat-pill-${cat.id}`}
                    type="button"
                    id={`filter-category-${cat.slug}`}
                    onClick={() => setSelectedCategory(isSelected ? 'all' : cat.id)}
                    className={`px-3.5 py-2 min-h-[44px] rounded-xl text-xs font-semibold transition-all cursor-pointer shrink-0 flex items-center justify-center gap-1.5 sm:gap-2 ${
                      isSelected
                        ? 'bg-slate-900 text-white shadow-xs ring-1 sm:ring-2 ring-slate-900/20'
                        : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 shadow-2xs'
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${
                        isSelected ? 'bg-indigo-400' : getCategoryDotStyle(cat.color)
                      } shrink-0`}
                    />
                    <span className="font-bold whitespace-nowrap">{cat.name}</span>
                    <span
                      className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono font-medium tabular-nums ${
                        isSelected ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {cat.activeCount}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active Category Spotlight - Compact Banner */}
          {selectedCategory !== 'all' && activeCategoryObj && (
            <div className="mt-2.5 sm:mt-3 p-3 sm:p-4 rounded-2xl sm:rounded-3xl bg-white border border-slate-200 shadow-2xs flex items-center justify-between gap-3 animate-in fade-in duration-200">
              <div className="min-w-0 flex-1 flex items-center gap-2 sm:gap-3">
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-[10px] font-bold shrink-0 ${getCategoryBadgeStyle(
                    activeCategoryObj.color
                  )}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${getCategoryDotStyle(activeCategoryObj.color)}`} />
                  {activeCategoryObj.name}
                </span>
                <span className="text-[11px] text-slate-500 truncate hidden sm:inline">
                  {activeCategoryObj.description || `${filteredPosts.length} artikel tersedia`}
                </span>
                <span className="text-[11px] text-slate-400 font-mono font-bold shrink-0 sm:hidden">
                  • {filteredPosts.length} artikel
                </span>
              </div>

              <button
                type="button"
                onClick={() => setSelectedCategory('all')}
                className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-xs transition cursor-pointer shrink-0 flex items-center gap-1"
                title="Reset kategori"
              >
                <X className="w-3 h-3" />
                <span className="hidden sm:inline">Reset</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Main Content Area with Smooth Transition */}
      <div
        key={`blog-content-${selectedCategory}-${filterMode}-${search}`}
        className="animate-blog-fade-in"
      >
        {/* Featured Article Section */}
        {filterMode === 'all' && !search && selectedCategory === 'all' && featuredPost && (
          <div className="mb-16">
            <div className="text-left mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-100 text-amber-800 text-[11px] font-extrabold uppercase tracking-wider">
                <Award className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                <span>Artikel Utama Pilihan Redaksi</span>
              </span>
            </div>

            <div className="group bg-slate-900 rounded-[2.5rem] overflow-hidden border border-slate-800 shadow-xl flex flex-col lg:flex-row relative">
              {/* Quick Bookmark Toggle Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleBookmark(featuredPost.slug, featuredPost.title);
                }}
                className={`absolute top-6 right-6 z-20 w-11 h-11 rounded-2xl flex items-center justify-center transition-all shadow-md cursor-pointer border ${
                  isBookmarked(featuredPost.slug)
                    ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600 scale-105'
                    : 'bg-white/10 backdrop-blur-md text-white border-white/10 hover:text-amber-500 hover:bg-white'
                }`}
                title={isBookmarked(featuredPost.slug) ? 'Hapus Bookmark' : 'Simpan ke Bookmark'}
              >
                <Bookmark className={`w-5 h-5 ${isBookmarked(featuredPost.slug) ? 'fill-current' : ''}`} />
              </button>

              {/* Thumbnail */}
              <div className="lg:w-1/2 aspect-video lg:aspect-auto bg-linear-to-br from-indigo-600 via-indigo-900 to-slate-950 p-8 sm:p-12 flex flex-col justify-end relative overflow-hidden min-h-[300px]">
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-15 transition-opacity">
                  <BookOpen className="w-48 h-44 text-white -rotate-12" />
                </div>
                <div className="relative z-10 space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-yellow-400 text-[10px] font-black text-slate-950 uppercase tracking-widest shadow-md shadow-amber-500/10 border border-amber-300">
                      <Award className="w-3.5 h-3.5 text-slate-950 fill-slate-950" />
                      ARTIKEL UTAMA
                    </span>
                    {(() => {
                      const cat = getPostCategory(featuredPost);
                      if (!cat) return null;
                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setSelectedCategory(cat.id);
                          }}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[10px] font-extrabold uppercase tracking-wider transition hover:opacity-90 cursor-pointer ${getCategoryBadgeStyle(
                            cat.color
                          )}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${getCategoryDotStyle(cat.color)}`} />
                          {cat.name}
                        </button>
                      );
                    })()}
                  </div>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight line-clamp-3 group-hover:text-indigo-200 transition-colors">
                    {featuredPost.title}
                  </h2>
                  {featuredPost.subtitle && (
                    <p className="text-sm sm:text-base text-indigo-200/80 font-medium line-clamp-2">
                      {featuredPost.subtitle}
                    </p>
                  )}
                  {featuredPost.tags && (
                    <div className="flex flex-wrap gap-1.5 pt-2 border-t border-white/10">
                      {featuredPost.tags.split(',').map(tag => tag.trim()).filter(Boolean).slice(0, 4).map((tag, tIdx) => (
                        <span
                          key={`f-tag-${tIdx}`}
                          className="inline-flex items-center text-[10px] font-medium text-indigo-300 uppercase tracking-wider"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Details & Summary */}
              <div className="lg:w-1/2 p-8 sm:p-12 flex flex-col justify-between bg-slate-950 text-slate-100">
                <div className="space-y-4">
                  <p className="text-slate-400 text-sm sm:text-base leading-relaxed line-clamp-4">
                    {featuredPost.summary || (featuredPost.content ? featuredPost.content.replace(/<[^>]*>/g, ' ').substring(0, 240) + '...' : 'Baca tulisan pilihan ini tentang ekosistem perbukuan.')}
                  </p>
                </div>

                <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-slate-300">
                      <User className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-bold text-white text-sm leading-none">{featuredPost.authorName}</p>
                      <p className="text-slate-400 text-xs mt-1.5 flex items-center gap-1 leading-none">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(featuredPost.publishedAt || featuredPost.createdAt).toLocaleDateString('id-ID', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  <a
                    href={`/blog/${featuredPost.slug}`}
                    onClick={(e) => {
                      if (onSelectPost) {
                        e.preventDefault();
                        onSelectPost(featuredPost.slug);
                      }
                    }}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl text-xs transition shadow-lg cursor-pointer self-start sm:self-auto"
                  >
                    <span>Baca Selengkapnya</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={`blog-skel-${i}`} className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden animate-pulse">
                <div className="aspect-video bg-slate-100" />
                <div className="p-6 space-y-4">
                  <div className="h-4 bg-slate-100 rounded w-1/4" />
                  <div className="h-6 bg-slate-100 rounded w-3/4" />
                  <div className="h-4 bg-slate-100 rounded w-full" />
                  <div className="h-4 bg-slate-100 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : regularPosts && regularPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularPosts.map((post, pIdx) => {
              const postCat = getPostCategory(post);

              return (
                <div
                  key={`blog-card-${post.id || post.slug}-${pIdx}`}
                  className="group bg-white rounded-3xl border border-slate-200 shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col relative"
                >
                  {/* Quick Bookmark Toggle Button on Card */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleBookmark(post.slug, post.title);
                    }}
                    className={`absolute top-4 right-4 z-20 w-9 h-9 rounded-xl flex items-center justify-center transition-all shadow-md cursor-pointer border ${
                      isBookmarked(post.slug)
                        ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600 scale-105'
                        : 'bg-white/90 backdrop-blur-md text-slate-500 border-slate-100 hover:text-amber-500 hover:bg-white'
                    }`}
                    title={isBookmarked(post.slug) ? 'Hapus Bookmark' : 'Simpan ke Bookmark'}
                  >
                    <Bookmark className={`w-4 h-4 ${isBookmarked(post.slug) ? 'fill-current' : ''}`} />
                  </button>

                  <a 
                    href={`/blog/${post.slug}`}
                    onClick={(e) => {
                      if (onSelectPost) {
                        e.preventDefault();
                        onSelectPost(post.slug);
                      }
                    }}
                    className="flex-1 flex flex-col cursor-pointer"
                  >
                    {/* Clean thumbnail with only title for an elegant look */}
                    <div className="aspect-video bg-linear-to-br from-indigo-600 via-indigo-900 to-slate-900 p-6 flex flex-col justify-end relative overflow-hidden">
                      <div className="absolute top-0 right-0 p-4 opacity-15 group-hover:opacity-25 transition-opacity">
                        <BookOpen className="w-24 h-24 text-white -rotate-12" />
                      </div>
                      <div className="relative z-10">
                        <h3 className="text-lg font-bold text-white leading-snug line-clamp-2 group-hover:text-indigo-200 transition-colors">
                          {post.title}
                        </h3>
                      </div>
                    </div>
                    
                    <div className="p-6 flex-1 flex flex-col">
                      {/* Category badge & tags */}
                      <div className="flex flex-wrap items-center gap-1.5 mb-3">
                        {postCat ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedCategory(postCat.id);
                            }}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-[11px] font-bold transition hover:opacity-80 cursor-pointer ${getCategoryBadgeStyle(
                              postCat.color
                            )}`}
                            title={`Filter kategori ${postCat.name}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${getCategoryDotStyle(postCat.color)}`} />
                            {postCat.name}
                          </button>
                        ) : null}

                        {post.tags && post.tags.split(',').map(tag => tag.trim()).filter(Boolean).slice(0, 2).map((tag, tIdx) => (
                          <span
                            key={`p-tag-${tag}-${tIdx}`}
                            className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-50 border border-slate-200 text-slate-500 font-medium text-[10px]"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                      
                      {post.subtitle && (
                        <p className="text-indigo-600 text-xs font-bold uppercase tracking-wider mb-2">
                          {post.subtitle}
                        </p>
                      )}
                      <p className="text-slate-600 text-sm line-clamp-3 mb-6 leading-relaxed">
                        {post.summary || (post.content ? post.content.replace(/<[^>]*>/g, ' ').substring(0, 160) + '...' : 'Baca artikel selengkapnya tentang strategi penulisan dan standar penerbitan di sistem UIBN.')}
                      </p>
                      
                      <div className="mt-auto pt-6 border-t border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                            <User className="w-4 h-4" />
                          </div>
                          <div className="text-[11px]">
                            <p className="font-bold text-slate-900 leading-none">{post.authorName}</p>
                            <p className="text-slate-500 mt-0.5 flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {new Date(post.publishedAt || post.createdAt).toLocaleDateString('id-ID', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        
                        <div className="text-indigo-600 group-hover:translate-x-1 transition-transform">
                          <ArrowRight className="w-5 h-5" />
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200 max-w-xl mx-auto px-6">
            <div className="w-14 h-14 rounded-2xl bg-white shadow-2xs border border-slate-200/80 flex items-center justify-center mx-auto mb-4 text-slate-400">
              {filterMode === 'bookmarks' ? (
                <Bookmark className="w-7 h-7 text-amber-500" />
              ) : selectedCategory !== 'all' ? (
                <Layers className="w-7 h-7 text-indigo-500" />
              ) : (
                <Search className="w-7 h-7 text-slate-400" />
              )}
            </div>
            <h3 className="text-lg font-bold text-slate-900">
              {filterMode === 'bookmarks'
                ? 'Belum ada artikel disimpan'
                : selectedCategory !== 'all'
                ? `Tidak ada artikel untuk kategori "${activeCategoryObj?.name || selectedCategory}"`
                : 'Tidak ada artikel ditemukan'}
            </h3>
            <p className="text-slate-500 mb-6 text-sm max-w-md mx-auto mt-1 leading-relaxed">
              {filterMode === 'bookmarks' 
                ? 'Bookmark tulisan favorit Anda dari bar menu atas artikel untuk membacanya nanti.' 
                : selectedCategory !== 'all'
                ? `Belum ditemukan artikel dalam kategori "${activeCategoryObj?.name || selectedCategory}"${search ? ` dan kata kunci "${search}"` : ''}. Coba pilih kategori lain atau reset filter.`
                : 'Coba periksa ejaan atau gunakan kata kunci pencarian yang lebih umum.'}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {selectedCategory !== 'all' && (
                <button
                  type="button"
                  onClick={() => setSelectedCategory('all')}
                  className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-bold text-xs shadow-md shadow-indigo-600/20 hover:bg-indigo-700 transition cursor-pointer flex items-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Tampilkan Semua Kategori</span>
                </button>
              )}
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 font-bold text-xs hover:bg-slate-50 transition cursor-pointer flex items-center gap-1.5"
                >
                  <X className="w-3.5 h-3.5" />
                  <span>Reset Kata Kunci</span>
                </button>
              )}
              {filterMode === 'bookmarks' && (
                <button
                  type="button"
                  onClick={() => setFilterMode('all')}
                  className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold transition-colors cursor-pointer text-xs"
                >
                  Jelajahi Semua Artikel
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Newsletter / CTA */}
      <div className="mt-24 p-8 sm:p-12 rounded-[2.5rem] bg-indigo-900 relative overflow-hidden text-center text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-indigo-500/20 via-transparent to-transparent" />
        <div className="relative z-10 space-y-6">
          <h2 className="text-3xl font-bold">Ingin mendapatkan update literasi terbaru?</h2>
          <p className="text-indigo-100 max-w-xl mx-auto">
            Berlangganan newsletter mingguan kami untuk mendapatkan panduan eksklusif seputar ekosistem UIBN.
          </p>
          <form onSubmit={handleSubscribeNewsletter} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input 
              type="email" 
              required
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder="Alamat email Anda" 
              className="flex-1 px-5 py-3 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-md focus:outline-none focus:ring-2 focus:ring-white/50 text-white placeholder:text-indigo-200/50"
              disabled={isSubmittingNewsletter}
            />
            <button 
              type="submit"
              disabled={isSubmittingNewsletter}
              className="px-6 py-3 rounded-2xl bg-white text-indigo-900 font-bold hover:bg-indigo-50 transition-colors shadow-lg disabled:opacity-50 cursor-pointer"
            >
              {isSubmittingNewsletter ? 'Memproses...' : 'Berlangganan'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
